import { useCallback, useEffect, useState } from "react";
import { Plus, Scale } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { AssetsCard } from "@/components/accounts/AssetsCard";
import { LiabilitiesCard } from "@/components/accounts/LiabilitiesCard";
import { AccountFormDialog } from "@/components/accounts/AccountFormDialog";
import { AdjustmentDialog } from "@/components/accounts/AdjustmentDialog";
import { AccountActions } from "@/components/accounts/AccountActions";
import { api, formatApiError } from "@/lib/api";
import { type Account, type AccountFamily, type AccountsResponse } from "@/lib/finance";
import { GoodsCard } from "@/components/accounts/GoodsCard";
import { AssetActionDialog, type AssetAction } from "@/components/accounts/AssetActionDialog";
import { AssetPurchaseWizard } from "@/components/accounts/AssetPurchaseWizard";
import { moneyIn } from "@/lib/format";
import { LoanBudgetPrompt, type NewLoan } from "@/components/accounts/LoanBudgetPrompt";
import { ClosedAccountsSection, monthYear } from "@/components/accounts/ClosedAccountsSection";

export default function AccountsPage() {
  const [data, setData] = useState<AccountsResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustId, setAdjustId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [preset, setPreset] = useState<AccountFamily | undefined>(undefined);
  const [buyOpen, setBuyOpen] = useState(false);
  const [assetAction, setAssetAction] = useState<AssetAction>(null);
  const [newLoan, setNewLoan] = useState<NewLoan | null>(null);

  const load = useCallback(() => {
    api.get<AccountsResponse>("/accounts", { params: { include_archived: true } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(load, [load]);
  const savedWithLoan = (loan?: NewLoan) => { load(); if (loan) setNewLoan(loan); };

  const archive = async (a: Account, archived: boolean) => {
    try {
      await api.post(`/accounts/${a.id}/archive`, { is_archived: archived });
      toast.success(archived ? `${a.name} archivada` : `${a.name} reactivada`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handlers = {
    onEdit: (a: Account) => { setPreset(undefined); setEditing(a); setFormOpen(true); },
    onAdjust: (a: Account) => { setAdjustId(a.id); setAdjustOpen(true); },
    onArchive: archive,
  };

  const active = data?.accounts.filter((a) => !a.is_archived) ?? [];
  const archived = data?.accounts.filter((a) => a.is_archived) ?? [];
  const onHand = active.filter((a) => a.account_type === "debit" || a.account_type === "cash");
  const invested = active.filter((a) => a.account_type === "savings" || a.account_type === "investment");
  const settled = active.filter((a) => a.account_type === "loan" && a.meta.settled_on);
  const disposed = active.filter((a) => a.account_type === "property" && a.disposed_on);
  const sum = (xs: Account[]) => Math.round(xs.reduce((s, a) => s + a.balance_mxn, 0) * 100) / 100;

  return (
    <section data-testid="accounts-page">
      <PageHeader title="Cuentas" testId="accounts-title" actions={<>
        <Button variant="outline" onClick={() => { setAdjustId(null); setAdjustOpen(true); }} className="h-11 border-[#DDE1E8] text-[#1B2A4A]" data-testid="open-adjustment-button">
          <Scale className="mr-2 h-4 w-4" /> Registrar ajuste
        </Button>
        <Button onClick={() => { setEditing(null); setPreset(undefined); setFormOpen(true); }} className="h-11 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="add-account-button">
          <Plus className="mr-2 h-4 w-4" /> Agregar cuenta
        </Button>
      </>} />
      {!data ? <div className="h-72 animate-pulse rounded-[14px] bg-white" /> : (
        <div className="fade-up space-y-6">
          <div className="min-w-0 space-y-6">
            <AssetsCard title="A la mano" testId="debit-card" empty="Aún no tienes cuentas de débito o efectivo." accounts={onHand} total={sum(onHand)} {...handlers} />
            <AssetsCard title="Lo que inviertes" testId="investment-card" empty="Aún no tienes cuentas de ahorro o inversión." accounts={invested} total={sum(invested)} {...handlers} />
            <GoodsCard accounts={active.filter((a) => a.account_type === "property" && !a.disposed_on)} total={data.goods_total ?? 0}
              onBuy={() => setBuyOpen(true)} onAdd={() => { setEditing(null); setPreset("property"); setFormOpen(true); }}
              onEdit={handlers.onEdit} onArchive={archive} onAction={setAssetAction} />
            <LiabilitiesCard accounts={active.filter((a) => a.is_liability && !a.meta.settled_on)} total={data.liabilities_total} {...handlers} />
            <ClosedAccountsSection title="Créditos liquidados" testId="settled-loans" accounts={settled} detail={(a) => `Liquidado en ${monthYear(a.meta.settled_on)}`} />
            <ClosedAccountsSection title="Bienes anteriores" testId="former-assets" accounts={disposed} detail={(a) => `Dado de baja en ${monthYear(a.disposed_on)}`} />
            {archived.length > 0 && (
              <div data-testid="archived-section">
                <button onClick={() => setShowArchived((s) => !s)} className="text-sm font-medium text-slate-500 hover:text-[#1B2A4A]" data-testid="toggle-archived-button">
                  {showArchived ? "Ocultar" : "Ver"} cuentas archivadas ({archived.length})
                </button>
                {showArchived && (
                  <ul className="mt-3 divide-y divide-[#DDE1E8] rounded-[14px] border border-[#DDE1E8] bg-white px-5">
                    {archived.map((a) => (
                      <li key={a.id} className="flex items-center justify-between gap-3 py-3 text-sm text-slate-500" data-testid={`archived-row-${a.id}`}>
                        <span className="truncate">{a.name}</span>
                        <span className="flex items-center gap-2"><span className="tabular">{moneyIn(a.current_balance, a.currency)}</span><AccountActions account={a} {...handlers} /></span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <AccountFormDialog open={formOpen} onOpenChange={setFormOpen} account={editing} onSaved={savedWithLoan} preset={preset} loans={active.filter((a) => a.account_type === "loan")} assets={active.filter((a) => a.account_type === "property")} onLoanPrompt={setNewLoan} />
      <AdjustmentDialog open={adjustOpen} onOpenChange={setAdjustOpen} accounts={active.filter((a) => a.account_type !== "property")} initialId={adjustId} onSaved={load} />
      <AssetPurchaseWizard open={buyOpen} onOpenChange={setBuyOpen} debitAccounts={active.filter((a) => a.account_type === "debit")} onSaved={savedWithLoan} />
      <LoanBudgetPrompt loan={newLoan} debits={active.filter((a) => a.account_type === "debit")} onClose={() => setNewLoan(null)} onSaved={load} />
      <AssetActionDialog action={assetAction} onClose={() => setAssetAction(null)} onSaved={load} />
    </section>
  );
}
